import { IShaderProgramInfo, initShaderProgram } from "./Shader";
import { mat4 } from "gl-matrix";
import { Camera } from "./Camera";
import { IRGBA, hexToIRGBA } from "./TimelineUtils";
import { IBaseTime } from "../redux/reducers/CrewScheduleReducer";
import { getLocalTimeMapAt } from "./TimelineLabels/BaseTimelineLabelsGL";
import moment from "moment-timezone";
const biddingBlockDayBackground = "#011414";
const biddingBlockWeekendBackground = "#032626";

interface IGLAttributes {
  position: WebGLBuffer | null;
}

//                   sec    min  hrs  days
const oneDayMillis = 1000 * 60 * 60 * 24;
export class ColPainter {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform float uXScale;
    uniform float uXOffset;
    uniform int uRowIndex;
    uniform vec4 uColor;

    varying lowp vec4 vColor;

    void main(void) {
      float newX = (aVertexPosition.x * uXScale) + uXOffset;
      vec4 position = vec4(newX, aVertexPosition.yzw );
      gl_Position = uProjectionMatrix * uModelViewMatrix * position;
      gl_Position.y = aVertexPosition.y;

      vColor = uColor;
    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying lowp vec4 vColor;

    void main(void) {
      gl_FragColor = vColor;
    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;
  private buffers: IGLAttributes;
  private positions: number[] = [];

  private biddingBlockDayBackground: IRGBA;
  private biddingBlockWeekendBackground: IRGBA;

  private modelViewMatrix: mat4;
  constructor(gl: WebGLRenderingContext) {
    // Initialize a shader program; this is where all the lighting
    // for the vertices and so forth is established.
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);

    this.biddingBlockDayBackground = hexToIRGBA(biddingBlockDayBackground);
    this.biddingBlockWeekendBackground = hexToIRGBA(biddingBlockWeekendBackground);

    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),

        xScale: gl.getUniformLocation(this.shaderProgram, "uXScale"),
        xOffset: gl.getUniformLocation(this.shaderProgram, "uXOffset"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };

    this.modelViewMatrix = mat4.create();
    mat4.translate(
      this.modelViewMatrix, // destination matrix
      this.modelViewMatrix, // matrix to translate
      [0.0, 0.0, -7.0]
    ); // amount to translate

    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //
  private initBuffers = (gl: WebGLRenderingContext): IGLAttributes => {
    // Create a buffer for the square's positions.
    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    // this.positions = [-1.0, 0.0, -1.0, 1.0, 1.0, 0.0, 1.0, 1.0];
    this.positions = [0.0, -1.0, 0.0, 1.0, 1.0, -1.0, 1.0, 1.0];
    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.STATIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  private drawColumn = (gl: WebGLRenderingContext, time: number, color: IRGBA): void => {
    const xOffset = time?.valueOf() - Camera.referenceTime;
    gl.uniform1f(this.programInfo.uniformLocations.xOffset, xOffset);

    gl.uniform4fv(this.programInfo.uniformLocations.color, [color.red, color.green, color.blue, color.alpha]);

    {
      const offset = 0;
      gl.drawArrays(gl.TRIANGLE_STRIP, offset, 4);
    }
  };

  public draw = (
    gl: WebGLRenderingContext,
    camera: Camera,
    dayScale: number,
    numDaysInView: number,
    color: IRGBA,
    baseTime: IBaseTime,
    biddingBlockTimes?: number[]
  ): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
      gl.vertexAttribPointer(
        this.programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
    }

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    // Set the shader uniforms
    const projectionMatrix = camera.getProjectionMatrix(gl);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, this.modelViewMatrix);

    const [minX, maxX] = camera.getXRange();
    const firstDayInView = new Date(minX);
    firstDayInView.setUTCHours(0);
    firstDayInView.setUTCMinutes(0);
    firstDayInView.setUTCSeconds(0);
    firstDayInView.setUTCMilliseconds(0);

    const numDays = Math.floor(minX / oneDayMillis);

    const local = baseTime.isUTC
      ? moment(firstDayInView).utc().subtract(1, "day")
      : getLocalTimeMapAt(baseTime.timezoneId, firstDayInView?.valueOf()).localMoment.clone().startOf("day");

    const startIndex = numDays % 2 ? 0 : 1;
    local.add(startIndex, "days");

    gl.uniform1f(this.programInfo.uniformLocations.xScale, dayScale);

    let count = 0;
    //14.0526	15.0640	22.9359
    const weekendColor: IRGBA = {
      red: color.red < 0.5 ? color.red + 0.08 : color.red - 0.08,
      green: color.green < 0.5 ? color.green + 0.08 : color.green - 0.08,
      blue: color.blue < 0.5 ? color.blue + 0.08 : color.blue - 0.08,
      alpha: 1.0,
    };

    while (local?.valueOf() <= maxX) {
      const insideBiddingPeriod =
        Array.isArray(biddingBlockTimes) &&
        local?.valueOf() >= biddingBlockTimes[0] &&
        local?.valueOf() < biddingBlockTimes[1]
          ? true
          : false;

      if (local.isoWeekday() === 6 || local.isoWeekday() === 7) {
        // WEEKENDS
        this.drawColumn(gl, local?.valueOf(), insideBiddingPeriod ? this.biddingBlockWeekendBackground : weekendColor);
      } else {
        // WEEK DAYS
        this.drawColumn(gl, local?.valueOf(), insideBiddingPeriod ? this.biddingBlockDayBackground : color);
      }

      local.add(1, "day");
      count++;
    }
  };
}
